import { Filter, FilterType } from '@helios/server-side-util'
import { SourceKey } from '../../models/source-key.model'

/**
 * The toolbar search box, as a SERVER-side filter on one column per source.
 *
 * Per the 2.0 design the box is not a free-text search over every column: it filters one identifier
 * column, and the request carries it like any other column filter, so it reaches the whole session
 * rather than only the loaded page.
 *
 *  - Base (Enfusion): **Instrument ID** — `instrumentId`, a `tran_data` key, so it routes under
 *    `filter.dynamicFilters`.
 *  - Comparison (Prometheus): **External Cusip** — `externalCusip`, a promoted column with a
 *    `TextFilter` slot on `FlexExternalTransactionsRequestFilters`, so it routes at the top level.
 *
 * Both go out as a TEXT `contains`, which is the design's substring match. For `instrumentId` that is
 * deliberate even though the catalog types it NUM: on a dynamic key `filterType` is the Postgres cast
 * target, a text cast cannot fail the way a numeric one does on a stray value, and `contains` still
 * finds a partial id. Routing comes from the catalog's `isDynamic`, as for every other filter.
 *
 * Clearwater has no read endpoint, so it has no search column.
 */
export const SEARCH_FIELD: Record<SourceKey, string | null> = {
  api: 'instrumentId',
  prom: 'externalCusip',
  cw: null,
}

/** What the box says it filters. */
export const SEARCH_PLACEHOLDER: Record<SourceKey, string> = {
  api: 'Filter by Instrument ID',
  prom: 'Filter by Asset ID',
  cw: 'Search',
}

/** The search box's contribution to one request: which column, and what to look for. */
export interface SearchConstraint {
  field: string
  value: string
}

/** One `contains` condition, in the shape `convertFilters` emits for a text filter. */
interface TextCondition {
  operator: 'contains'
  value: string
}

/** A converted text filter, as far as merging needs to read it. */
interface TextFilterBody extends Filter {
  operator?: string
  filters?: unknown[]
}

/**
 * The search box's filter for its column, folded into whatever that column's own header filter put
 * there — the backend takes one filter per field.
 *
 *  - No header filter: a single-condition text `contains`.
 *  - A text header filter joined with AND: the search becomes one more AND condition, so both apply.
 *  - Anything else (a number / date / set header filter, or a two-condition OR): the two cannot be
 *    expressed as one filter, so the search takes the wire. The header filter still narrows the
 *    returned page client-side, so the grid shows rows that satisfy both.
 */
export function withSearchCondition(
  existing: Filter | undefined,
  value: string,
): Filter {
  const condition: TextCondition = { operator: 'contains', value }
  const current = existing as TextFilterBody | undefined
  if (
    current?.filterType === FilterType.Text &&
    current.operator === 'AND' &&
    Array.isArray(current.filters)
  ) {
    const merged: TextFilterBody = {
      ...current,
      filters: [...current.filters, condition],
    }
    return merged
  }
  const fresh: TextFilterBody = {
    filterType: FilterType.Text,
    operator: 'AND',
    filters: [condition],
  }
  return fresh
}
